import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { getUserId } from './utils/userId';
import CrosswordGrid from './components/CrosswordGrid';
import LetterBank from './components/LetterBank';
import SummaryBackside from './components/SummaryBackside';
import './App.css';
import useViewportHeight from './hooks/useViewportHeight';
import useKeyboardNavigation from './hooks/useKeyboardNavigation';
import InstructionsModal from './components/InstructionsModal';
import StatsStrip from './components/StatsStrip';
import storage from './utils/storage';
import { todayPuzzleId, isSamePuzzleId, shiftPuzzleId } from './utils/puzzleId';

import usePuzzle from './hooks/usePuzzle';
import PuzzleShell from './components/PuzzleShell';
import StartCurtain from './components/StartCurtain';
import ArchiveBrowser from './components/ArchiveBrowser';
import usePuzzleDateParam from './hooks/usePuzzleDateParam';

// Seconds added to the clock per assist. Sized against the summary
// histogram, which buckets at 10s up to a 300s cap: much above ~30s a go and
// every assisted solve lands in the overflow bin.
const ASSIST_COST = { check: 15, tile: 30, answer: 60 };

function App() {
  // Initialize the viewport height hook
  useViewportHeight();

  const [tiles, setTiles] = useState([]);
  const [usedTileIds, setUsedTileIds] = useState(new Set());
  const initializedRef = useRef(false);
  const [grid, setGrid] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState('across');

  // Track placement history for undo functionality
  const placementHistoryRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);

  // Timer states
  const [isComplete, setIsComplete] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  // The how-to-play never opens by itself. It used to open on every load, then
  // on first visit only; now a first-time player gets a small pointer at the
  // "How to play" link on the start curtain instead of a full-screen wall.
  const [showInstructions, setShowInstructions] = useState(false);

  // Whether to show that pointer. Cleared once the player opens the rules, and
  // suppressed for anyone with completion history — they are demonstrably not
  // new, whether or not they ever read them.
  const [hasSeenInstructions, setHasSeenInstructions] = useState(
    () => storage.get(storage.KEYS.SEEN_INSTRUCTIONS, false)
  );

  const openInstructions = useCallback(() => {
    storage.set(storage.KEYS.SEEN_INSTRUCTIONS, true);
    setHasSeenInstructions(true);
    setShowInstructions(true);
  }, []);

  // The board is withheld until the player taps Play (see StartCurtain), which
  // is both what starts the clock and what stops them studying the puzzle
  // beforehand. First interaction also starts it, as a safety net.
  const [hasStarted, setHasStarted] = useState(false);
  const startPlaying = useCallback(() => setHasStarted(true), []);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // New state for flipping the card
  const [isFlipped, setIsFlipped] = useState(false);

  // Add performanceData state
  const [performanceData, setPerformanceData] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  // State for user puzzle history
  const [userPuzzleHistory, setUserPuzzleHistory] = useState(null);
  // State for today's puzzle leaderboard (only populated if user already completed it)
  const [todayPuzzlePerformances, setTodayPuzzlePerformances] = useState(null);
  
  // State for placed words (for keyboard navigation)
  const [placedWords, setPlacedWords] = useState([]);
  
  // State for hint navigation (lifted from CrosswordGrid for keyboard support)
  const [currentHintIndex, setCurrentHintIndex] = useState(0);

  // Assist ladder state. Declared up here with the rest of the state because
  // handleCompletion reads assistsUsed and is defined before the assist
  // handlers are.
  const [assistsUsed, setAssistsUsed] = useState({ check: 0, tile: 0, answer: 0 });
  // Whether the puzzle ended by giving up, so the summary can say so rather
  // than congratulating someone on a solve they did not make.
  const [endedByGiveUp, setEndedByGiveUp] = useState(false);
  // Transient: the cells a check flagged as wrong.
  const [wrongCells, setWrongCells] = useState(null);
  // Drives the floating "+0:30" over the timer.
  const [penaltyFlash, setPenaltyFlash] = useState(null);

  const wrongCellsTimeoutRef = useRef(null);
  const penaltyTimeoutRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(wrongCellsTimeoutRef.current);
    clearTimeout(penaltyTimeoutRef.current);
  }, []);
  // Which puzzle: today, or an archive date from ?d=.
  const { puzzleId: archivePuzzleId, isArchive, setPuzzleId } = usePuzzleDateParam();
  const [showArchive, setShowArchive] = useState(false);

  // Fetched as a frozen snapshot rather than generated here.
  const { status: puzzleStatus, puzzle, error: puzzleError, retry: retryPuzzle } =
    usePuzzle(archivePuzzleId);

  // Ground truth for the completion check. Previously private to CrosswordGrid.
  const solutionRef = useRef(null);

  /**
   * Load a snapshot into play state.
   *
   * Replaces handleSolutionUpdate, which generated the tiles, locked cells and
   * grid in the browser off two seeded RNG streams. All of that now happens
   * offline; this just unpacks what the server sent.
   */
  const hydrateFromSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;

    solutionRef.current = snapshot.solutionGrid;
    setPlacedWords(snapshot.placedWords);
    setTiles(snapshot.tiles);
    // Fresh copy each time, so clearing cannot alias the cached snapshot.
    setGrid(snapshot.grid.map((row) => row.map((cell) => ({ ...cell }))));

    setUsedTileIds(new Set());
    placementHistoryRef.current = [];
    setCanUndo(false);
    setSelectedCell(null);
    setCurrentHintIndex(0);
    setIsComplete(false);
    setAssistsUsed({ check: 0, tile: 0, answer: 0 });
    setEndedByGiveUp(false);
    setWrongCells(null);
    setTimer(0);
    // A different puzzle is a different solve: curtain back up, clock at zero.
    setHasStarted(false);
    initializedRef.current = snapshot.puzzleId;
  }, []);

  // Hydrate once per puzzle.
  useEffect(() => {
    if (puzzleStatus !== 'ready' || !puzzle) return;
    if (initializedRef.current === puzzle.puzzleId) return;
    hydrateFromSnapshot(puzzle);
  }, [puzzleStatus, puzzle, hydrateFromSnapshot]);


  const handleTileRemoval = useCallback((tileId) => {
    setUsedTileIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(tileId);
      // console.log('Tile removed:', tileId);
      return newSet;
    });

  }, []);

  /**
   * Return a tile to the bank and clear every cell it occupies.
   *
   * Shared by double-click removal and by the assist reveals. The "every cell"
   * part is what makes it the right primitive for reveals: a multi-letter tile
   * can straddle two intersecting words, so revealing one word may need to
   * evict a tile whose other letters sit outside it. Clearing only the cells
   * inside the word would strand the rest of the tile on the board while the
   * tile itself went back to the bank.
   */
  const evictTile = useCallback((tileId) => {
    if (tileId == null || tileId < 0) return;

    handleTileRemoval(tileId);

    placementHistoryRef.current = placementHistoryRef.current.filter(
      placement => placement.tileId !== tileId
    );
    setCanUndo(placementHistoryRef.current.length > 0);

    setGrid(prevGrid =>
      prevGrid.map(gridRow =>
        gridRow.map(gridCell => {
          if (gridCell.tileId === tileId) {
            return {
              ...gridCell,
              letter: '',
              tileId: null,
              cellDisplay: gridCell.isBlocked ? 'blocked' : (gridCell.isLocked ? 'locked' : 'open')
            };
          }
          return gridCell;
        })
      )
    );
  }, [handleTileRemoval]);

  const handleCellDoubleClick = useCallback((row, col) => {
    const cell = grid[row][col];

    // Revealed cells are not tiles and must not be removable.
    if (cell.tileId == null || cell.isRevealed) return;

    evictTile(cell.tileId);
  }, [grid, evictTile]);

  const handleLetterPlaced = useCallback((tileId, isReturning = false, clickPosition = null) => {
    setUsedTileIds(prev => {
      const newSet = new Set(prev);
      if (isReturning) {
        newSet.delete(tileId);
      } else {
        newSet.add(tileId);
      }
      return newSet;
    });

    const cellToUse = clickPosition || selectedCell;
    if (!isReturning && cellToUse) {
      const tile = tiles.find(t => t.id === tileId);
      if (tile) {
        // Track placement for undo
        const cellsAffected = [];
        const { row, col } = cellToUse;
        
        if (tile.type === 'single') {
          cellsAffected.push({ row, col });
        } else if (tile.type === 'double') {
          if (tile.direction === 'horizontal') {
            cellsAffected.push({ row, col }, { row, col: col + 1 });
          } else {
            cellsAffected.push({ row, col }, { row: row + 1, col });
          }
        } else if (tile.type === 'triple') {
          if (tile.direction === 'horizontal') {
            cellsAffected.push({ row, col }, { row, col: col + 1 }, { row, col: col + 2 });
          } else {
            cellsAffected.push({ row, col }, { row: row + 1, col }, { row: row + 2, col });
          }
        }
        
        // Add to placement history
        placementHistoryRef.current.push({
          tileId,
          cells: cellsAffected,
          tile
        });
        setCanUndo(true);
        
        setGrid(prevGrid => {
          if (!prevGrid) return prevGrid;
          const newGrid = prevGrid.map(row => row.map(cell => ({ ...cell }))); // Deep copy

          if (tile.type === 'single') {
            newGrid[row][col] = {
              ...newGrid[row][col],
              letter: tile.letters[0],
              tileId: tileId,
              cellDisplay: 'single'
            };
          } else if (tile.type === 'double') {
            if (tile.direction === 'horizontal') {
              newGrid[row][col] = {
                ...newGrid[row][col],
                letter: tile.letters[0],
                tileId: tileId,
                cellDisplay: 'horizontalLeft'
              };
              newGrid[row][col + 1] = {
                ...newGrid[row][col + 1],
                letter: tile.letters[1],
                tileId: tileId,
                cellDisplay: 'horizontalRight'
              };
            } else if (tile.direction === 'vertical') {
              newGrid[row][col] = {
                ...newGrid[row][col],
                letter: tile.letters[0],
                tileId: tileId,
                cellDisplay: 'verticalTop'
              };
              newGrid[row + 1][col] = {
                ...newGrid[row + 1][col],
                letter: tile.letters[1],
                tileId: tileId,
                cellDisplay: 'verticalBottom'
              };
            }
          } else if (tile.type === 'triple') {
            if (tile.direction === 'horizontal') {
              newGrid[row][col] = {
                ...newGrid[row][col],
                letter: tile.letters[0],
                tileId: tileId,
                cellDisplay: 'horizontalLeft'
              };
              newGrid[row][col + 1] = {
                ...newGrid[row][col + 1],
                letter: tile.letters[1],
                tileId: tileId,
                cellDisplay: 'horizontalMiddle'
              };
              newGrid[row][col + 2] = {
                ...newGrid[row][col + 2],
                letter: tile.letters[2],
                tileId: tileId,
                cellDisplay: 'horizontalRight'
              };
            } else if (tile.direction === 'vertical') {
              newGrid[row][col] = {
                ...newGrid[row][col],
                letter: tile.letters[0],
                tileId: tileId,
                cellDisplay: 'verticalTop'
              };
              newGrid[row + 1][col] = {
                ...newGrid[row + 1][col],
                letter: tile.letters[1],
                tileId: tileId,
                cellDisplay: 'verticalMiddle'
              };
              newGrid[row + 2][col] = {
                ...newGrid[row + 2][col],
                letter: tile.letters[2],
                tileId: tileId,
                cellDisplay: 'verticalBottom'
              };
            }
          }

          // After setting cellDisplay properties, update the tile class based on cellDisplay
          Object.keys(newGrid).forEach(gridRow => {
            newGrid[gridRow].forEach(gridCell => {
              if (gridCell.tileId === tileId) {
                // Assuming you have a function or a mechanism to set the CSS class based on cellDisplay
                // For example, you might add a `className` property to each cell
                gridCell.className = gridCell.cellDisplay;
              }
            });
          });

          // Log the updated grid
          // console.log('Grid after tile placement:', newGrid);

          // Determine the number of steps to move based on tile type
          let steps = 1;
          if (tile.type === 'double') steps = 2;
          if (tile.type === 'triple') steps = 3;
          const direction = selectedDirection;
          let newRow = row;
          let newCol = col;

          if (direction === 'across') {
            newCol += steps;
          } else if (direction === 'down') {
            newRow += steps;
          }

          /**
           * Helper function to find the next open cell in the specified direction.
           *
           * @param {number} startRow - Starting row index.
           * @param {number} startCol - Starting column index.
           * @param {string} direction - Direction to search ('across' or 'down').
           * @param {array} grid - Current grid state.
           * @returns {object|null} - Returns the next open cell as { row, col } or null if none found.
           */
          const findNextOpenCell = (startRow, startCol, direction, grid) => {
            let tempRow = startRow;
            let tempCol = startCol;

            while (tempRow < grid.length && tempCol < grid[0].length) {
              if (direction === 'across') {
                if (!grid[tempRow][tempCol].isBlocked && !grid[tempRow][tempCol].letter) {
                  return { row: tempRow, col: tempCol };
                }
                tempCol += 1;
              } else if (direction === 'down') {
                if (!grid[tempRow][tempCol].isBlocked && !grid[tempRow][tempCol].letter) {
                  return { row: tempRow, col: tempCol };
                }
                tempRow += 1;
              }
            }

            return null; // No open cell found
          };

          /**
           * Helper function to find the next open word in the puzzle.
           * 
           * @param {array} grid - Current grid state.
           * @returns {object|null} - Returns { row, col, direction } of next word or null if none found.
           */
          const findNextOpenWord = (grid) => {
            // First try to find words with empty first letters
            for (let r = 0; r < grid.length; r++) {
              for (let c = 0; c < grid[0].length; c++) {
                const cell = grid[r][c];
                
                // Skip if cell is blocked or already has a letter
                if (cell.isBlocked || cell.letter) continue;

                // Check if this cell starts a word horizontally
                if (c === 0 || grid[r][c-1].isBlocked) {
                  // Look ahead to see if this is part of a word
                  let isWord = false;
                  let nextCol = c + 1;
                  while (nextCol < grid[0].length && !grid[r][nextCol].isBlocked) {
                    isWord = true;
                    nextCol++;
                  }
                  if (isWord) return { row: r, col: c, direction: 'across' };
                }

                // Check if this cell starts a word vertically
                if (r === 0 || grid[r-1][c].isBlocked) {
                  // Look down to see if this is part of a word
                  let isWord = false;
                  let nextRow = r + 1;
                  while (nextRow < grid.length && !grid[nextRow][c].isBlocked) {
                    isWord = true;
                    nextRow++;
                  }
                  if (isWord) return { row: r, col: c, direction: 'down' };
                }
              }
            }

            // If no words with empty first letters found, find any empty cell in a word
            for (let r = 0; r < grid.length; r++) {
              for (let c = 0; c < grid[0].length; c++) {
                const cell = grid[r][c];
                
                if (cell.isBlocked || cell.letter) continue;

                // Check if this cell is part of a horizontal word
                if ((c > 0 && !grid[r][c-1].isBlocked) || 
                    (c < grid[0].length-1 && !grid[r][c+1].isBlocked)) {
                  return { row: r, col: c, direction: 'across' };
                }

                // Check if this cell is part of a vertical word
                if ((r > 0 && !grid[r-1][c].isBlocked) || 
                    (r < grid.length-1 && !grid[r+1][c].isBlocked)) {
                  return { row: r, col: c, direction: 'down' };
                }
              }
            }

            return null; // No open words found
          };

          // Check if the new cell is within bounds and not blocked
          if (newRow < newGrid.length && newCol < newGrid[0].length && !newGrid[newRow][newCol].isBlocked) {
            if (newGrid[newRow][newCol].letter) {
              // If the cell is occupied, find the next open cell
              const nextOpen = findNextOpenCell(newRow, newCol, direction, newGrid);
              if (nextOpen) {
                setSelectedCell(nextOpen);
              } else {
                // Optionally handle the case where no open cell is found
                setSelectedCell(null);
                // console.log('No open cells available in the current direction.');
              }
            } else {
              // If the cell is open, select it
              setSelectedCell({ row: newRow, col: newCol });
            }
          } else {
            // Optionally handle the case where the new cell is out of bounds or blocked
            const nextWord = findNextOpenWord(newGrid);
            if (nextWord) {
              setSelectedCell({ row: nextWord.row, col: nextWord.col });
              setSelectedDirection(nextWord.direction);
            } else {
              setSelectedCell(null);
              // console.log('No open words available.');
            }
          }

          return newGrid;
        });
      }
    }
  }, [selectedCell, tiles, selectedDirection]);

  // Reset the board to its starting state. This used to null the grid so the
  // generation effect would rebuild it; with snapshots that would mean a
  // network request on every reset, so rehydrate from the cached puzzle.
  const handleClear = useCallback(() => {
    if (puzzle) {
      initializedRef.current = null;
      hydrateFromSnapshot(puzzle);
      return;
    }
    setUsedTileIds(new Set());
    setGrid(null);
    setSelectedCell(null);
    setIsComplete(false);
    placementHistoryRef.current = [];
    setCanUndo(false);
  }, [puzzle, hydrateFromSnapshot]);

  // Undo function - removes the last tile placement
  const handleUndo = useCallback(() => {
    if (placementHistoryRef.current.length === 0 || !grid) return;

    // Get the last placement
    const lastPlacement = placementHistoryRef.current.pop();
    if (!lastPlacement) return;
    
    // Update canUndo state
    setCanUndo(placementHistoryRef.current.length > 0);

    const { tileId, cells } = lastPlacement;

    // Remove tile from usedTileIds
    setUsedTileIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(tileId);
      return newSet;
    });

    // Clear the cells on the grid
    setGrid(prevGrid => {
      if (!prevGrid) return prevGrid;
      const newGrid = prevGrid.map(row => row.map(cell => ({ ...cell }))); // Deep copy

      cells.forEach(({ row, col }) => {
        const cell = newGrid[row][col];
        if (cell && cell.tileId === tileId) {
          // Only clear if not locked
          if (!cell.isLocked) {
            newGrid[row][col] = {
              ...cell,
              letter: '',
              tileId: null,
              cellDisplay: cell.isBlocked ? 'blocked' : 'open'
            };
          }
        }
      });

      return newGrid;
    });
  }, [grid]);

  const handleTileClick = useCallback((tile, overrideCell = null) => {
    const cellToUse = overrideCell || selectedCell;
    if (!cellToUse || !grid) return;

    startPlaying();

    const { row, col } = cellToUse;
    
    // For single tiles, maintain existing behavior
    if (tile.type === 'single') {
      handleLetterPlaced(tile.id, false, cellToUse);
      return;
    }

    // For multi-tiles, check direction compatibility and space availability
    const tileLength = tile.letters.length;
    const isHorizontal = tile.direction === 'horizontal';
    
    // Check if tile direction matches selected direction
    if ((isHorizontal && selectedDirection !== 'across') || 
        (!isHorizontal && selectedDirection !== 'down')) {
      // console.log('Tile direction does not match selected direction');
      return;
    }

    // Check if all required cells are available
    let allCellsAvailable = true;
    const cellsToFill = [];

    for (let i = 0; i < tileLength; i++) {
      const checkRow = isHorizontal ? row : row + i;
      const checkCol = isHorizontal ? col + i : col;

      // Check if position is within grid bounds
      if (checkRow >= grid.length || checkCol >= grid[0].length) {
        allCellsAvailable = false;
        break;
      }

      const cell = grid[checkRow][checkCol];
      if (!cell || cell.isBlocked || cell.isLocked || cell.letter) {
        allCellsAvailable = false;
        break;
      }

      cellsToFill.push({ row: checkRow, col: checkCol });
    }

    // If all cells are available, place the tile
    if (allCellsAvailable) {
      // Track placement for undo
      placementHistoryRef.current.push({
        tileId: tile.id,
        cells: cellsToFill.map(c => ({ row: c.row, col: c.col })),
        tile
      });
      setCanUndo(true);
      
      const newGrid = grid.map(row => row.map(cell => ({ ...cell }))); // Deep copy
      
      // Fill all cells with their respective letters and set tileId and cellDisplay
      cellsToFill.forEach((cell, index) => {
        let cellDisplay = '';

        if (tile.type === 'double') {
          if (tile.direction === 'horizontal') {
            if (index === 0) cellDisplay = 'horizontalLeft';
            if (index === 1) cellDisplay = 'horizontalRight';
          } else if (tile.direction === 'vertical') {
            if (index === 0) cellDisplay = 'verticalTop';
            if (index === 1) cellDisplay = 'verticalBottom';
          }
        }

        if (tile.type === 'triple') {
          if (tile.direction === 'horizontal') {
            if (index === 0) cellDisplay = 'horizontalLeft';
            if (index === 1) cellDisplay = 'horizontalMiddle';
            if (index === 2) cellDisplay = 'horizontalRight';
          } else if (tile.direction === 'vertical') {
            if (index === 0) cellDisplay = 'verticalTop';
            if (index === 1) cellDisplay = 'verticalMiddle';
            if (index === 2) cellDisplay = 'verticalBottom';
          }
        }

        newGrid[cell.row][cell.col] = {
          ...newGrid[cell.row][cell.col],
          letter: tile.letters[index],
          tileId: tile.id,
          cellDisplay: tile.type === 'single' ? 'single' : cellDisplay
        };
      });

      // Log the updated grid
      // console.log('Grid after tile placement:', newGrid);

      setGrid(newGrid);
      handleLetterPlaced(tile.id, false);
    } else {
      // console.log('Cannot place tile: some cells are not available');
    }
  }, [selectedCell, grid, selectedDirection, handleLetterPlaced, startPlaying]);

  // **New Function: Handle Completion**
  const handleCompletion = useCallback((overrideTime = null, { gaveUp = false } = {}) => {
    // The snapshot's own id, so an archive replay is filed against the date it
    // belongs to rather than today.
    const puzzle_id = puzzle?.puzzleId ?? todayPuzzleId();
    const context = isArchive ? 'archive' : 'daily';

    const userId = getUserId();
    const completion_time = overrideTime !== null ? overrideTime : timer;
    setEndedByGiveUp(gaveUp);

    // A give-up finishes the puzzle with no tiles necessarily placed, so it
    // has to bypass the "did they actually do anything" guard.
    if (usedTileIds.size > 0 || gaveUp || overrideTime !== null) {
      setIsComplete(true);
      setTimeout(() => {
        setIsFlipped(true);
      }, 2000);
      // Send user ID to the backend
      fetch(`${backendUrl}/api/logCompletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId, puzzle_id, completion_time, gave_up: gaveUp, context,
          assists_used: assistsUsed.check + assistsUsed.tile + assistsUsed.answer,
        })
      })
      .then(response => response.json())
      .then(data => {
        // Archive runs are recorded but never ranked, so there is no
        // leaderboard to fetch — and fetching one would show the player a
        // board they are deliberately not on. Guarded here as well as on the
        // server so the client is correct even against an older backend.
        if (context === 'archive') {
          setPerformanceData([]);
          setTotalTime(completion_time);
          return;
        }

        fetch(`${backendUrl}/api/getPuzzlePerformances?puzzle_id=${puzzle_id}&user_id=${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
          // **Removed Body from GET Request**
        })
        .then(response => response.json())
        .then(performanceData => {
          // console.log('Puzzle performances:', performanceData);
          
          // **Set performanceData state instead of individual states**
          setPerformanceData(performanceData.performances);
          setTotalTime(completion_time);
          
          // console.log(`Performance data updated.`);
        })
        .catch(error => {
          console.error('Error getting puzzle performances:', error);
        });
      })
      .catch(error => {
        console.error('Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      });
    } else {
    }
  }, [usedTileIds, backendUrl, timer, assistsUsed, puzzle, isArchive]);

  // Reroll the player's leaderboard name. Resolves to { displayName } or
  // { retryAfterSeconds } when the once-a-day limit applies; the board is
  // relabelled in place rather than refetched.
  const handleRename = useCallback(async () => {
    const response = await fetch(`${backendUrl}/api/renamePlayer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: getUserId() }),
    });
    const data = await response.json();
    if (response.ok && data.displayName) {
      const relabel = rows => Array.isArray(rows)
        ? rows.map(row => (row.is_you ? { ...row, display_name: data.displayName } : row))
        : rows;
      setPerformanceData(relabel);
      setTodayPuzzlePerformances(relabel);
    }
    return data;
  }, [backendUrl]);

  useEffect(() => {
    const userId = getUserId();
    // console.log('Backend URL:', backendUrl);
    // console.log('User ID:', userId);

    fetch(`${backendUrl}/api/logVisit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId })
    })
    .then(response => response.json())
    .then(data => {
      // console.log('Visit logged successfully:', data);
    })
    .catch(error => {
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    });

    // Fetch user's puzzle history
    fetch(`${backendUrl}/api/getUserPuzzleHistory?user_id=${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      // console.log('User puzzle history fetched successfully:', data);
      setUserPuzzleHistory(data);

      // If the user already played today, fetch today's leaderboard so the
      // stats strip can show their standing.
      const currentPuzzleId = todayPuzzleId();
      const completedToday = data.userPuzzleHistory?.some(
        entry => isSamePuzzleId(entry.puzzle_id, currentPuzzleId)
      );

      if (completedToday) {
        fetch(`${backendUrl}/api/getPuzzlePerformances?puzzle_id=${currentPuzzleId}&user_id=${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        .then(response => response.json())
        .then(performanceData => {
          setTodayPuzzlePerformances(performanceData.performances);
        })
        .catch(error => {
          console.error('Error fetching today\'s puzzle performances:', error);
        });
      }
    })
    .catch(error => {
      console.error('Error fetching user puzzle history:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    });
  }, [backendUrl]);

  // Add formatTime function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // Hint navigation functions for keyboard support
  const handleNextHint = useCallback(() => {
    if (placedWords.length === 0) return;
    setCurrentHintIndex(prev =>
      prev === placedWords.length - 1 ? 0 : prev + 1
    );
  }, [placedWords.length]);

  const handlePrevHint = useCallback(() => {
    if (placedWords.length === 0) return;
    setCurrentHintIndex(prev =>
      prev === 0 ? placedWords.length - 1 : prev - 1
    );
  }, [placedWords.length]);

  // Selecting a cell or placing a tile is what starts the clock (see hasStarted).
  const handleCellSelect = useCallback((cell) => {
    if (cell) startPlaying();
    setSelectedCell(cell);
  }, [startPlaying]);

  // Add-to-home-screen. Chrome fires this instead of prompting; holding it
  // lets the offer appear on the post-solve screen, which is a far better
  // moment to make it than page load.
  const installPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [canNotify, setCanNotify] = useState(
    () => typeof Notification !== 'undefined' && Notification.permission === 'default'
  );

  useEffect(() => {
    const onBeforeInstall = (event) => {
      event.preventDefault();
      installPromptRef.current = event;
      const dismissedAt = storage.get(storage.KEYS.A2HS_DISMISSED_AT, 0);
      // Do not re-offer for a month after a dismissal.
      setCanInstall(Date.now() - dismissedAt > 30 * 24 * 60 * 60 * 1000);
    };
    const onInstalled = () => setCanInstall(false);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    const prompt = installPromptRef.current;
    if (!prompt) return;
    setCanInstall(false);
    storage.set(storage.KEYS.A2HS_DISMISSED_AT, Date.now());
    prompt.prompt();
    installPromptRef.current = null;
  }, []);

  // Permission capture only. Actually delivering a daily nudge needs VAPID
  // keys, subscription storage and a cron, which is separate work.
  const handleNotify = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    try {
      await Notification.requestPermission();
    } finally {
      setCanNotify(Notification.permission === 'default');
    }
  }, []);

  // Which cells the player did not fill themselves, for the share grid.
  const assistedKeys = useMemo(() => {
    const keys = new Set();
    if (!grid) return keys;
    grid.forEach((row, r) => row.forEach((cell, c) => {
      if (cell.isRevealed) keys.add(`${r},${c}`);
    }));
    return keys;
  }, [grid]);

  const assistCount = assistsUsed.check + assistsUsed.tile + assistsUsed.answer;

  // Long-form date for the start curtain, from the snapshot rather than the
  // browser clock so it always names the puzzle actually being served.
  const puzzleDateLabel = useMemo(() => {
    if (!puzzle?.date) return '';
    const [y, m, d] = puzzle.date.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
    });
  }, [puzzle]);

  const isFirstTimeVisitor = useMemo(() => {
    if (hasSeenInstructions) return false;
    const history = userPuzzleHistory?.userPuzzleHistory;
    // Still loading, or genuinely no history: treat as new.
    return !Array.isArray(history) || history.length === 0;
  }, [hasSeenInstructions, userPuzzleHistory]);

  const hasPlayedToday = useMemo(() => {
    const history = userPuzzleHistory?.userPuzzleHistory;
    if (!Array.isArray(history) || !puzzle?.puzzleId) return false;
    return history.some((entry) => isSamePuzzleId(entry.puzzle_id, puzzle.puzzleId));
  }, [userPuzzleHistory, puzzle]);

  // ---------------------------------------------------------------- assists
  //
  // Three graded helps between "stuck" and "give up", which previously was the
  // only option and ended the puzzle outright. Each still counts as a solve,
  // each costs time, and the cost is visible rather than silent.
  //
  /** Every cell's correct letter, from the snapshot's placements. */
  const solutionMap = useMemo(() => {
    const map = new Map();
    placedWords.forEach(({ word, direction, startRow, startCol }) => {
      for (let i = 0; i < word.length; i++) {
        const row = direction === 'across' ? startRow : startRow + i;
        const col = direction === 'across' ? startCol + i : startCol;
        map.set(`${row},${col}`, word[i]);
      }
    });
    return map;
  }, [placedWords]);

  const applyPenalty = useCallback((kind) => {
    const cost = ASSIST_COST[kind];
    setTimer(prev => prev + cost);
    setAssistsUsed(prev => ({ ...prev, [kind]: prev[kind] + 1 }));

    setPenaltyFlash(cost);
    clearTimeout(penaltyTimeoutRef.current);
    penaltyTimeoutRef.current = setTimeout(() => setPenaltyFlash(null), 1600);
  }, [setTimer]);

  const clearWrongCells = useCallback(() => {
    clearTimeout(wrongCellsTimeoutRef.current);
    setWrongCells(null);
  }, []);

  /** Flag placed tiles that are in the wrong place. Non-destructive. */
  const handleCheckGrid = useCallback(() => {
    if (!grid || isComplete) return;

    const wrong = new Set();
    grid.forEach((row, r) => {
      row.forEach((cell, c) => {
        // Only judge what the player actually placed: skip empties, locked
        // cells (correct by construction) and anything already revealed.
        if (cell.tileId == null || cell.isLocked || cell.isRevealed) return;
        if (cell.letter !== solutionMap.get(`${r},${c}`)) wrong.add(`${r},${c}`);
      });
    });

    setWrongCells(wrong);
    applyPenalty('check');

    clearTimeout(wrongCellsTimeoutRef.current);
    wrongCellsTimeoutRef.current = setTimeout(() => setWrongCells(null), 5000);
  }, [grid, isComplete, solutionMap, applyPenalty]);

  /**
   * Reveal a set of cells by placing the tiles that belong in them.
   *
   * Reveals place real tiles rather than painting synthetic letters. Painting
   * looked simpler and was wrong: the bank is sized exactly to the empty cells,
   * so removing a cell from the board without removing its tile leaves a
   * surplus letter that can never be placed — and if the revealed cell was part
   * of a multi-letter tile's span, that tile can never be placed either, since
   * placement needs the whole span empty. Most cells are covered by multi-letter
   * tiles, so this was the common case, not an edge case.
   *
   * Each tile knows the span it was cut from, so revealing means: find the tiles
   * whose spans touch the target cells, evict whatever is in their way, and put
   * them down. A tile is placed whole, which can fill a little more than was
   * asked for — the right trade, since a tile is the unit the game deals in.
   */
  const revealCells = useCallback((cells) => {
    if (!grid) return false;

    // Cells already revealed or locked are correct and stay as they are.
    const wanted = new Set(
      cells
        .filter(({ row, col }) => {
          const cell = grid[row]?.[col];
          return cell && !cell.isBlocked && !cell.isLocked && !cell.isRevealed;
        })
        .map(({ row, col }) => `${row},${col}`)
    );
    if (wanted.size === 0) return false;

    // Every tile belonging to one of those cells that is not already sitting
    // correctly — whether it is in the bank or misplaced elsewhere on the
    // board. Reclaiming misplaced ones matters: otherwise revealing a word
    // whose tile the player had put somewhere else would leave a hole that
    // nothing in the bank could fill.
    const toPlace = tiles.filter((tile) => {
      if (!tile.cells.some((c) => wanted.has(`${c.row},${c.col}`))) return false;
      const alreadyRight = tile.cells.every((c) => grid[c.row]?.[c.col]?.tileId === tile.id);
      return !alreadyRight;
    });
    if (toPlace.length === 0) return false;

    const span = new Set();
    toPlace.forEach((tile) => tile.cells.forEach((c) => span.add(`${c.row},${c.col}`)));

    // Anything sitting on that span has to come off first — including tiles
    // whose other letters lie outside it, or they would be stranded.
    const inTheWay = new Set();
    span.forEach((key) => {
      const [r, c] = key.split(',').map(Number);
      const cell = grid[r][c];
      // Never evict an already-revealed tile: it is correct, and returning it
      // to the bank would leave the board short a tile it still needs.
      if (cell.isRevealed) return;
      if (cell.tileId != null && cell.tileId >= 0) inTheWay.add(cell.tileId);
    });
    // Plus the tiles being placed, if they are currently down in the wrong spot.
    toPlace.forEach((tile) => { if (usedTileIds.has(tile.id)) inTheWay.add(tile.id); });
    inTheWay.forEach(evictTile);

    const placedIds = new Set(toPlace.map((t) => t.id));
    setUsedTileIds((prev) => new Set([...prev, ...placedIds]));

    // Revealed placements are not undoable: they were not the player's move,
    // and undoing one would put a tile back in the bank that the board still
    // needs. They are therefore kept out of the placement history.
    setGrid((prevGrid) => {
      const next = prevGrid.map((row) => row.map((cell) => ({ ...cell })));
      toPlace.forEach((tile) => {
        tile.cells.forEach(({ row, col }, i) => {
          const isMulti = tile.letters.length > 1;
          const last = tile.letters.length - 1;
          next[row][col] = {
            ...next[row][col],
            letter: tile.letters[i],
            tileId: tile.id,
            isRevealed: true,
            cellDisplay: !isMulti
              ? 'single'
              : tile.direction === 'horizontal'
                ? (i === 0 ? 'horizontalLeft' : i === last ? 'horizontalRight' : 'horizontalMiddle')
                : (i === 0 ? 'verticalTop' : i === last ? 'verticalBottom' : 'verticalMiddle'),
          };
        });
      });
      return next;
    });

    clearWrongCells();
    return true;
  }, [grid, tiles, usedTileIds, evictTile, clearWrongCells]);

  /** Reveal the selected cell. */
  const handleRevealTile = useCallback(() => {
    if (!grid || isComplete || !selectedCell) return;
    if (revealCells([selectedCell])) applyPenalty('tile');
  }, [grid, isComplete, selectedCell, revealCells, applyPenalty]);

  /**
   * Reveal the whole answer currently shown in the clue bar.
   *
   * Fills every cell of the word, not only the empty ones: a word left holding
   * a wrong crossing letter is not revealed, and the completion scan would
   * never fire.
   */
  const handleRevealAnswer = useCallback(() => {
    if (!grid || isComplete) return;
    const target = placedWords[currentHintIndex];
    if (!target) return;

    const { word, direction, startRow, startCol } = target;
    const cells = [];
    for (let i = 0; i < word.length; i++) {
      cells.push({
        row: direction === 'across' ? startRow : startRow + i,
        col: direction === 'across' ? startCol + i : startCol,
      });
    }

    if (revealCells(cells)) applyPenalty('answer');
  }, [grid, isComplete, placedWords, currentHintIndex, revealCells, applyPenalty]);

  // Wrong-cell flags describe the board at the instant Check ran, so any
  // placement, removal or undo invalidates them. usedTileIds moves on all four.
  useEffect(() => {
    setWrongCells(null);
  }, [usedTileIds]);

  // Reveal the whole solution and finish, flagged as a give-up.
  //
  // Lives here because App owns the solution. Records real elapsed time — the
  // old version forced 600 seconds, which is what put give-ups on the
  // leaderboard as 10:00 finishes.
  const handleGiveUp = useCallback(() => {
    if (!grid || !solutionRef.current || isComplete) return;

    clearInterval(timerRef.current);

    const solution = solutionRef.current;
    setGrid((prev) =>
      prev.map((row, r) =>
        row.map((cell, c) => {
          if (cell.isBlocked) return cell;
          return {
            ...cell,
            letter: solution[r][c].letter,
            // tileId stays null; isRevealed marks these cells instead of the
            // old -1 sentinel, which slipped past handleCellDoubleClick's
            // null check and let one double-click wipe the revealed board.
            tileId: null,
            isRevealed: true,
            cellDisplay: 'single',
          };
        })
      )
    );

    setTimeout(() => handleCompletion(timer, { gaveUp: true }), 100);
  }, [grid, isComplete, timer, handleCompletion, timerRef]);

  // Completion check.
  //
  // Moved up from CrosswordGrid, which owned the solution privately. Every
  // non-blocked cell must match. The `isComplete` guard matters: handleCompletion
  // depends on `timer`, so its identity changes every second and this effect
  // re-runs with it — previously only ON CONFLICT DO NOTHING stopped that from
  // posting a completion once per tick.
  useEffect(() => {
    if (isComplete || !grid || !solutionRef.current) return;

    const solution = solutionRef.current;
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < grid[row].length; col++) {
        const target = solution[row]?.[col];
        if (!target || target.isBlocked) continue;
        if (grid[row][col].letter !== target.letter) return;
      }
    }

    handleCompletion();
  }, [grid, isComplete, handleCompletion]);

  // Keyboard navigation hook
  useKeyboardNavigation({
    grid,
    tiles,
    usedTileIds,
    selectedCell,
    selectedDirection,
    onCellSelect: handleCellSelect,
    setSelectedDirection,
    handleTileClick,
    onCellDoubleClick: handleCellDoubleClick,
    placedWords,
    isComplete,
    isPaused,
    isFlipped,
    showInstructions,
    onNextHint: handleNextHint,
    onPrevHint: handlePrevHint
  });

  return (
    <div className="App">
      {showInstructions && (
        <InstructionsModal onClose={() => setShowInstructions(false)} />
      )}
      {showArchive && (
        <ArchiveBrowser
          userPuzzleHistory={userPuzzleHistory}
          currentPuzzleId={puzzle?.puzzleId}
          onSelect={(id) => { setShowArchive(false); setPuzzleId(id); }}
          onClose={() => setShowArchive(false)}
        />
      )}
      <main className="App-main">
        <StatsStrip
          userPuzzleHistory={userPuzzleHistory}
          todayPuzzlePerformances={todayPuzzlePerformances}
          onShowInstructions={openInstructions}
          showHelp={hasStarted}
          archiveDate={isArchive ? puzzle?.date : null}
          onBackToToday={() => setPuzzleId(null)}
        />
        <div className={`flip-card ${isFlipped ? 'flipped' : ''}`}>
          <div className="flip-card-inner">
            <div className="flip-card-front">
              {puzzleStatus !== 'ready' || !grid ? (
                <PuzzleShell
                  status={puzzleStatus}
                  error={puzzleError}
                  onRetry={retryPuzzle}
                />
              ) : !hasStarted ? (
                <StartCurtain
                  date={puzzleDateLabel}
                  alreadyPlayed={hasPlayedToday}
                  onStart={startPlaying}
                  isFirstTime={isFirstTimeVisitor}
                  isArchive={isArchive}
                  onShowInstructions={openInstructions}
                  onShowArchive={() => setShowArchive(true)}
                />
              ) : (
              <CrosswordGrid
                grid={grid}
                placedWords={placedWords}
                onClear={handleClear}
                tiles={tiles}
                usedTileIds={usedTileIds}
                isFlipped={isFlipped}
                setIsFlipped={setIsFlipped}
                isComplete={isComplete}
                selectedCell={selectedCell}
                onCellSelect={handleCellSelect}
                setGrid={setGrid}
                selectedDirection={selectedDirection}
                setSelectedDirection={setSelectedDirection}
                onCellDoubleClick={handleCellDoubleClick}
                timerActive={!showInstructions && hasStarted && grid != null && placedWords.length > 0}
                timer={timer}
                setTimer={setTimer}
                isPaused={isPaused}
                setIsPaused={setIsPaused}
                formatTime={formatTime}
                timerRef={timerRef}
                currentHintIndex={currentHintIndex}
                onNextHint={handleNextHint}
                onPrevHint={handlePrevHint}
                onGiveUp={handleGiveUp}
                onCheckGrid={handleCheckGrid}
                onRevealTile={handleRevealTile}
                onRevealAnswer={handleRevealAnswer}
                assistCosts={ASSIST_COST}
                penaltyFlash={penaltyFlash}
                wrongCells={wrongCells}
                onUndo={handleUndo}
                canUndo={canUndo}
              />
              )}
            </div>
            <div className="flip-card-back">
              <SummaryBackside 
                setIsFlipped={setIsFlipped} 
                setIsPaused={setIsPaused}
                isComplete={isComplete}
                performanceData={performanceData}
                onRename={handleRename}
                totalTime={totalTime}
                formatTime={formatTime}
                userPuzzleHistory={userPuzzleHistory}
                puzzleId={puzzle?.puzzleId}
                grid={grid}
                assistedKeys={assistedKeys}
                assistCount={assistCount}
                isArchive={isArchive}
                gaveUp={endedByGiveUp}
                canInstall={canInstall}
                onInstall={handleInstall}
                canNotify={canNotify}
                onNotify={handleNotify}
                canPlayYesterday={!isArchive}
                onPlayYesterday={() => setPuzzleId(shiftPuzzleId(todayPuzzleId(), -1))}
              />
            </div>
          </div>
        </div>
        {!isFlipped && hasStarted && (
          <LetterBank 
            tiles={tiles.filter(tile => !usedTileIds.has(tile.id))} 
            usedTileIds={usedTileIds}
            onTileClick={handleTileClick}
          />
        )}
      </main>
    </div>
  );
}

export default App;